import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdmin } from "./AdminLayout";
import { useAdminLang, STATUS_LABELS, SOURCE_LABELS } from "./adminLang";
import { Download, X } from "lucide-react";

const Leads = () => {
  const { canLeads } = useAdmin();
  const { lang, tr } = useAdminLang();
  const SL = STATUS_LABELS[lang];
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], users: [] });
  const [filters, setFilters] = useState({ status: "", source: "", q: "" });
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/admin/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.source]);

  const openDetail = (id) => api.get(`/admin/leads/${id}`).then((r) => setDetail(r.data)).catch(() => {});
  const patch = async (data) => {
    await api.patch(`/admin/leads/${detail.lead.id}`, data);
    openDetail(detail.lead.id); load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/leads/${detail.lead.id}/notes`, { note });
    setNote(""); openDetail(detail.lead.id);
  };
  const exportCsv = async () => {
    const res = await api.get("/admin/leads/export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const inputCls = "bg-white/5 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF6600]";

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-black text-white">Leads</h1>
        {canLeads && (
          <button onClick={exportCsv} data-testid="admin-export-csv-button"
            className="inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-full px-5 py-2.5 transition-colors">
            <Download size={14} /> {tr("Exporter CSV", "Export CSV")}
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <input placeholder={tr("Rechercher…", "Search…")} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load()} data-testid="leads-search-input" className={`${inputCls} w-56`} />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="leads-filter-status" className={inputCls}>
          <option value="">{tr("Tous statuts", "All statuses")}</option>
          {Object.entries(SL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} data-testid="leads-filter-source" className={inputCls}>
          <option value="">{tr("Toutes sources", "All sources")}</option>
          {["coverage", "quote", "contact", "application", "newsletter"].map((s) => <option key={s} value={s}>{SOURCE_LABELS[lang][s]}</option>)}
        </select>
        <button onClick={load} className="text-xs font-bold text-[#FF9900] border border-white/15 rounded-xl px-4 hover:border-[#FF6600]" data-testid="leads-search-button">OK</button>
      </div>

      <div className="mt-6 bg-[#101214] border border-white/10 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-lead-pipeline-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10">
              <th className="px-5 py-3.5">{tr("Nom", "Name")}</th><th className="px-5 py-3.5">{tr("Contact", "Contact")}</th><th className="px-5 py-3.5">{tr("Source", "Source")}</th>
              <th className="px-5 py-3.5">{tr("Service", "Service")}</th><th className="px-5 py-3.5">{tr("Statut", "Status")}</th><th className="px-5 py-3.5">{tr("Date", "Date")}</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} onClick={() => openDetail(l.id)} data-testid={`lead-row-${l.id}`}
                className="border-b border-white/5 hover:bg-white/[0.04] cursor-pointer transition-colors">
                <td className="px-5 py-3.5 font-bold text-white">{l.name || "—"}<span className="block text-[11px] font-normal text-white/40">{l.company}</span></td>
                <td className="px-5 py-3.5 text-white/60 text-xs">{l.email}<span className="block">{l.phone}</span></td>
                <td className="px-5 py-3.5 text-white/60 text-xs">{SOURCE_LABELS[lang][l.source] || l.source}</td>
                <td className="px-5 py-3.5 text-white/60 text-xs max-w-[160px] truncate">{l.service}</td>
                <td className="px-5 py-3.5"><span className="text-[11px] font-bold bg-[#FF6600]/15 text-[#FF9900] rounded-full px-2.5 py-1">{SL[l.status] || l.status}</span></td>
                <td className="px-5 py-3.5 text-white/40 text-xs">{new Date(l.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR")}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-white/35 text-sm">{tr("Aucun lead.", "No leads.")}</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" data-testid="lead-detail-panel" onClick={() => setDetail(null)}>
          <div className="w-full max-w-lg bg-[#101214] border-l border-white/10 h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-black text-white">{detail.lead.name}</h2>
                <p className="text-xs text-white/45 mt-1">{detail.lead.email} · {detail.lead.phone}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-white/50 hover:text-white p-1" data-testid="lead-detail-close"><X size={20} /></button>
            </div>

            {canLeads && (
              <div className="mt-5 grid grid-cols-2 gap-3">
                <select value={detail.lead.status} onChange={(e) => patch({ status: e.target.value })} data-testid="lead-status-select" className={inputCls}>
                  {Object.entries(SL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={detail.lead.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} data-testid="lead-assign-select" className={inputCls}>
                  <option value="">{tr("Non assigné", "Unassigned")}</option>
                  {meta.users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
              </div>
            )}

            {detail.source_record && (
              <div className="mt-6 bg-white/[0.04] border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#FF9900]">{tr("Demande d'origine", "Source request")} ({detail.lead.source})</h3>
                <dl className="mt-3 space-y-1.5">
                  {Object.entries(detail.source_record.payload || {}).filter(([, v]) => v).slice(0, 14).map(([k, v]) => (
                    <div key={k} className="text-xs"><dt className="inline text-white/40">{k}: </dt><dd className="inline text-white/75">{String(v)}</dd></div>
                  ))}
                  {detail.source_record.department && <div className="text-xs"><dt className="inline text-white/40">department: </dt><dd className="inline text-white/75">{detail.source_record.department}</dd></div>}
                </dl>
              </div>
            )}

            <div className="mt-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-white/40">{tr("Notes internes", "Internal notes")}</h3>
              <div className="mt-3 space-y-3">
                {detail.notes.map((n) => (
                  <div key={n.id} className="bg-white/[0.04] rounded-xl p-3 text-xs text-white/75">
                    {n.note}<span className="block mt-1 text-white/30">{new Date(n.created_at).toLocaleString(lang === "en" ? "en-GB" : "fr-FR")}</span>
                  </div>
                ))}
                {detail.notes.length === 0 && <p className="text-xs text-white/30">{tr("Aucune note.", "No notes.")}</p>}
              </div>
              {canLeads && (
                <div className="mt-3 flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={tr("Ajouter une note…", "Add a note…")} data-testid="lead-note-input" className={`${inputCls} flex-1`} />
                  <button onClick={addNote} data-testid="lead-add-note-button"
                    className="bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-xl px-4 transition-colors">+</button>
                </div>
              )}
            </div>

            <div className="mt-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-white/40">{tr("Historique des statuts", "Status history")}</h3>
              <ul className="mt-3 space-y-2">
                {detail.history.map((h) => (
                  <li key={h.id} className="text-xs text-white/55">
                    {SL[h.from_status] || h.from_status} → <span className="text-[#FF9900] font-bold">{SL[h.to_status] || h.to_status}</span>
                    <span className="text-white/30"> · {new Date(h.created_at).toLocaleString(lang === "en" ? "en-GB" : "fr-FR")}</span>
                  </li>
                ))}
                {detail.history.length === 0 && <li className="text-xs text-white/30">{tr("Aucun changement.", "No changes.")}</li>}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
